import { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  ProductCategory,
  ProductResponse,
} from "../../../types/product.type";
import {
  createProduct,
  duplicateProduct,
  getProductDetails,
  updateProduct,
  uploadProductImages,
} from "../../../services";
import { featureAccess } from "../../../permissions/access";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import DeleteProductButton from "./DeleteProductButton";
import ProductCategoryDialog from "./ProductCategoryDialog";
import ProductDrawerFields, {
  ProductTextChangeEvent,
} from "./ProductDrawerFields";
import FacePriceImpactDialog from "./FacePriceImpactDialog";
import { useFacePriceImpact } from "./useFacePriceImpact";
import {
  INITIAL_PRODUCT_FORM,
  ProductForm,
  ProductFormErrors,
  buildProductCreatePayload,
  buildProductPayload,
  buildProductUpdatePayload,
  mapProductToForm,
  validateProductForm,
} from "./productForm";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

/** Head office's catalogue is mirrored to every branch, so any save reaches all of them. */
const BRANCH_IMPACT_MESSAGE = "การแก้ไขสินค้านี้จะมีผลกับทุกสาขา";

export type ProductDrawerMode = "create" | "edit" | "duplicate";

const DRAWER_TITLE: Record<ProductDrawerMode, string> = {
  create: "เพิ่มรายการสินค้า",
  edit: "แก้ไขรายการสินค้า",
  duplicate: "ทำสำเนารายการสินค้า",
};

interface ProductDrawerProps {
  open: boolean;
  mode: ProductDrawerMode;
  productUuid?: string;
  categories: ProductCategory[];
  onClose: () => void;
  onSaved: () => void;
  /** The product this drawer edited no longer exists, so its screen must move on. */
  onDeleted: () => void;
  /** Re-reads the category list after the drawer adds one to it. */
  onReloadCategories: () => Promise<void>;
}

/** Create, edit and duplicate share one form: same fields, same source product. */
export default function ProductDrawer({
  open,
  mode,
  productUuid,
  categories,
  onClose,
  onSaved,
  onDeleted,
  onReloadCategories,
}: ProductDrawerProps) {
  const role = useUserRole();
  const canEditCatalogue = featureAccess.productWrite(role);

  const [form, setForm] = useState<ProductForm>(INITIAL_PRODUCT_FORM);
  const [errors, setErrors] = useState<ProductFormErrors>({});
  // The record as loaded: its pricebooks are listed under the form, and the delete dialog
  // reads how far a delete reaches from it.
  const [storedProduct, setStoredProduct] = useState<ProductResponse | null>(
    null,
  );
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  // A failed detail read leaves the form on its empty defaults, so saving it would
  // blank the stored product. Saving stays blocked until a read succeeds.
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  // Set once the drawer has created a product (a create, or the copy a duplicate
  // produced), so a retry updates that product instead of creating a second one.
  const [createdUuid, setCreatedUuid] = useState<string>("");
  const [imageUploadFailed, setImageUploadFailed] = useState<boolean>(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState<boolean>(false);
  const facePriceImpact = useFacePriceImpact();

  const isDuplicate = mode === "duplicate";
  // Once the copy exists the drawer edits it, so it stops behaving as a duplicate.
  const isCopyPending = isDuplicate && !createdUuid;
  const isCodeEditable = mode === "create" && !createdUuid;
  const canDelete = mode === "edit" && canEditCatalogue;
  const storedFacePrice = storedProduct?.face_price ?? null;

  const resetState = useCallback(() => {
    setForm(INITIAL_PRODUCT_FORM);
    setErrors({});
    setStoredProduct(null);
    setThumbnailFile(null);
    setPictureFile(null);
    setCreatedUuid("");
    setImageUploadFailed(false);
    setLoadingDetails(false);
    setLoadFailed(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetState();
    if (!productUuid) return;

    // Reopening the drawer on another product, or closing it mid-flight, must not
    // let the older response overwrite the state the newer one owns.
    let ignored = false;

    const loadProduct = async () => {
      setLoadingDetails(true);
      try {
        const {
          data: { data },
        } = await getProductDetails(productUuid);
        if (ignored) return;
        const product: ProductResponse = data;
        const loadedForm = mapProductToForm(product);
        // The copy is issued its own code, so the source's must not be shown as
        // if it were the new product's.
        setForm(isDuplicate ? { ...loadedForm, code: "" } : loadedForm);
        setStoredProduct(product);
      } catch {
        // The axios interceptor reports the failure; the flag keeps the empty form
        // out of a save so the stored product cannot be blanked by a retry.
        if (!ignored) setLoadFailed(true);
      } finally {
        if (!ignored) setLoadingDetails(false);
      }
    };

    loadProduct();

    return () => {
      ignored = true;
    };
  }, [open, productUuid, isDuplicate, resetState]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleTextChange =
    (field: keyof ProductForm) => (event: ProductTextChangeEvent) => {
      const { value } = event.target;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

  const handleAmountChange = (field: keyof ProductForm) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleToggleStatus = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setForm((prev) => ({ ...prev, status: checked ? "enable" : "disable" }));
  };

  const handleToggleExport = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setForm((prev) => ({ ...prev, allowExport: checked }));
  };

  const handleToggleExcludeFromWaste = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setForm((prev) => ({ ...prev, excludeFromWaste: checked }));
  };

  const handleSelectThumbnail = (file: File) => setThumbnailFile(file);

  const handleClearThumbnail = () => {
    setThumbnailFile(null);
    setForm((prev) => ({ ...prev, thumbnailUrl: null }));
  };

  const handleSelectPicture = (file: File) => setPictureFile(file);

  const handleClearPicture = () => {
    setPictureFile(null);
    setForm((prev) => ({ ...prev, pictureUrl: null }));
  };

  const handleOpenCategoryDialog = () => setCategoryDialogOpen(true);

  const handleCloseCategoryDialog = () => setCategoryDialogOpen(false);

  const handleCategoryCreated = async (categoryCode: string) => {
    // The select can only show the new category once the reloaded list holds it.
    await onReloadCategories();
    setForm((prev) => ({ ...prev, categoryCode }));
    setErrors((prev) => ({ ...prev, categoryCode: "" }));
  };

  const validate = () => {
    const newErrors = validateProductForm(form, isCodeEditable);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadPickedImages = async (uuid: string): Promise<boolean> => {
    if (!thumbnailFile && !pictureFile) return true;
    try {
      await uploadProductImages(uuid, {
        thumbnail: thumbnailFile ?? undefined,
        picture: pictureFile ?? undefined,
      });
      return true;
    } catch {
      // The product itself is saved, so report the partial result instead of
      // closing the drawer as if everything went through.
      return false;
    }
  };

  /** Closes the drawer, or keeps it open on the warning an image failure needs. */
  const finishSave = async (savedUuid: string) => {
    const uploaded = await uploadPickedImages(savedUuid);
    onSaved();
    if (!uploaded) {
      setImageUploadFailed(true);
      return;
    }
    handleClose();
  };

  const submitProduct = async (applyFacePriceTo: string[]) => {
    // The copy the drawer just made owns the retry, not the product it came from.
    const existingUuid = createdUuid || productUuid || "";
    setSaving(true);
    setImageUploadFailed(false);
    try {
      let savedUuid = existingUuid;
      if (existingUuid) {
        await updateProduct(
          existingUuid,
          buildProductUpdatePayload(
            form,
            thumbnailFile,
            pictureFile,
            storedFacePrice,
            applyFacePriceTo,
          ),
        );
        // A retry after a failed image upload must compare against the face price now stored.
        setStoredProduct((prev) =>
          prev ? { ...prev, face_price: Number(form.facePrice) } : prev,
        );
      } else {
        const {
          data: { data },
        } = await createProduct(
          buildProductCreatePayload(form, thumbnailFile, pictureFile),
        );
        savedUuid = data.uuid;
        setCreatedUuid(savedUuid);
      }

      await finishSave(savedUuid);
    } catch {
      // The axios interceptor reports the failure; keep the drawer open so the
      // user can correct the input and retry.
    } finally {
      setSaving(false);
    }
  };

  const submitDuplicate = async () => {
    if (!productUuid) return;
    setSaving(true);
    setImageUploadFailed(false);
    try {
      const {
        data: { data },
      } = await duplicateProduct(
        productUuid,
        buildProductPayload(form, thumbnailFile, pictureFile),
      );
      const copy: ProductResponse = data;
      setCreatedUuid(copy.uuid);
      await finishSave(copy.uuid);
    } catch {
      // Reported globally by the axios interceptor.
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (isCopyPending) {
      await submitDuplicate();
      return;
    }
    // Only a stored product can sit in other pricebooks at its old face price.
    if (mode === "edit" && productUuid) {
      const isHeld = await facePriceImpact.holdSaveForChoice(
        productUuid,
        storedFacePrice,
        form.facePrice,
      );
      if (isHeld) return;
    }
    await submitProduct([]);
  };

  const handleConfirmFacePriceImpact = async () => {
    await submitProduct(facePriceImpact.dialogState.selectedCodes);
    facePriceImpact.dialogState.onClose();
  };

  const handleDeleted = () => {
    onDeleted();
    onClose();
  };

  // The form still holds the status it loaded with, so it closes rather than let a later
  // save switch the product back on.
  const handleDeactivated = () => {
    onSaved();
    onClose();
  };

  const priceBookItems = storedProduct?.pricebookItems ?? [];
  // Without a delete action the footer has one group left, which belongs on the right.
  const footerAlignment = canDelete ? "space-between" : "flex-end";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: { width: { xs: "100%", md: 720 }, maxWidth: "100%", p: 0 },
        },
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box sx={{ p: 3, pb: 0, [compact]: { p: 2, pb: 0 } }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            {DRAWER_TITLE[mode]}
          </Typography>
          <Divider />
        </Box>

        <Box sx={{ p: 3, [compact]: { p: 2 }, overflowY: "auto", flex: 1 }}>
          {loadingDetails ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              {loadFailed && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  โหลดข้อมูลสินค้าไม่สำเร็จ จึงยังบันทึกไม่ได้
                  กรุณาปิดหน้าต่างนี้แล้วเปิดใหม่อีกครั้ง
                </Alert>
              )}
              <Alert severity="warning" sx={{ mb: 2 }}>
                {BRANCH_IMPACT_MESSAGE}
              </Alert>
              {imageUploadFailed && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  บันทึกข้อมูลสินค้าแล้ว แต่อัปโหลดรูปภาพไม่สำเร็จ
                  กรุณาเลือกรูปภาพใหม่แล้วกดบันทึกอีกครั้ง
                </Alert>
              )}
              {isCopyPending && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  ระบบจะสร้างสินค้าใหม่จากข้อมูลด้านล่าง
                  แก้ไขข้อมูลก่อนกดทำสำเนาได้ตามต้องการ
                  โดยรหัสสินค้าใหม่จะออกให้อัตโนมัติ
                </Alert>
              )}

              <ProductDrawerFields
                form={form}
                errors={errors}
                categories={categories}
                priceBookItems={priceBookItems}
                isCodeEditable={isCodeEditable}
                isDuplicate={isDuplicate}
                canCreateCategory={canEditCatalogue}
                thumbnailFile={thumbnailFile}
                pictureFile={pictureFile}
                onTextChange={handleTextChange}
                onAmountChange={handleAmountChange}
                onToggleStatus={handleToggleStatus}
                onToggleExport={handleToggleExport}
                onToggleExcludeFromWaste={handleToggleExcludeFromWaste}
                onSelectThumbnail={handleSelectThumbnail}
                onClearThumbnail={handleClearThumbnail}
                onSelectPicture={handleSelectPicture}
                onClearPicture={handleClearPicture}
                onAddCategory={handleOpenCategoryDialog}
              />
            </>
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent={footerAlignment}
          gap={2}
          sx={{
            p: 3,
            pt: 2,
            [compact]: { p: 2, pt: 1.5 },
            borderTop: (theme) => `1px solid ${theme.palette.draft.main}`,
            bgcolor: (theme) => theme.palette.background.paper,
          }}
        >
          {canDelete && (
            <DeleteProductButton
              product={storedProduct}
              disabled={saving || loadingDetails}
              onDeleted={handleDeleted}
              onDeactivated={handleDeactivated}
            />
          )}
          <Stack direction="row" gap={2}>
            <Button disabled={saving} onClick={handleClose}>
              ยกเลิก
            </Button>
            <Button
              loading={saving || facePriceImpact.checking}
              disabled={loadingDetails || loadFailed}
              variant="contained"
              onClick={handleSubmit}
            >
              {isCopyPending ? "ทำสำเนา" : "บันทึก"}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <FacePriceImpactDialog
        {...facePriceImpact.dialogState}
        saving={saving}
        onConfirm={handleConfirmFacePriceImpact}
      />
      <ProductCategoryDialog
        open={categoryDialogOpen}
        onClose={handleCloseCategoryDialog}
        onSaved={handleCategoryCreated}
      />
    </Drawer>
  );
}
